import React, { useState } from 'react';
import { VerticalTimeline, VerticalTimelineElement } from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';
import { ClipboardPen, DollarSign, Handshake, ClipboardCheck, Calendar, FileText } from 'lucide-react';
import { Milestone } from './Contract.interface';
import './Timeline.css';
import { useUser } from '../../hooks';

interface TimelineProps {
    milestones: Milestone[];
    homeownerOnRight?: boolean;
}

const Timeline: React.FC<TimelineProps> = ({ milestones, homeownerOnRight = false }) => {
    const { currentUserId } = useUser();
    const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());

    const toggleDescription = (milestoneId: string) => {
        setExpandedDescriptions(prev => {
            const newSet = new Set(prev);
            if (newSet.has(milestoneId)) {
                newSet.delete(milestoneId);
            } else {
                newSet.add(milestoneId);
            }
            return newSet;
        });
    };
   
    const renderDescription = (description: string, milestoneId: string) => {
        const isExpanded = expandedDescriptions.has(milestoneId);
        const shouldTruncate = description.length > 300;
    
        if (!shouldTruncate) {
            return (
                <div className="timeline-description mt-1 text-gray-600">
                    {description.split('\n').map((paragraph, index) => (
                        <p key={index}>{paragraph}</p>
                    ))}
                </div>
            );
        }
    
        const displayText = isExpanded ? description : description.slice(0, 300);
        const paragraphs = displayText.split('\n');
    
        return (
            <div className="timeline-description mt-1">
                <div className="text-gray-600">
                    {paragraphs.map((paragraph, index) => (
                        <p key={index} style={{ display: 'inline' }}>
                            {paragraph}
                            {index < paragraphs.length - 1 && <br />}
                        </p>
                    ))}
                    {!isExpanded && '... '}
                    <button
                        onClick={() => toggleDescription(milestoneId)}
                        className="btn btn-sm btn-soft-secondary"
                    >
                        {isExpanded ? 'Show Less' : 'Show More'}
                    </button>
                </div>
            </div>
        );
    };

    const getIconStyle = (type: string): React.CSSProperties => {
        switch (type) {
            case 'signing':
                return { background: 'rgb(33, 150, 243)', color: '#fff' }; // Blue
            case 'payment':
                return { background: 'rgb(16, 204, 82)', color: '#fff' }; // Green
            case 'inspection':
                return { background: 'rgb(255, 164, 29)', color: '#fff' }; // Orange
            case 'addendum':
                return { background: 'rgb(233, 30, 99)', color: '#fff' }; // Pink
            case 'submit':
                return { background: 'rgb(241, 123, 114)', color: '#fff' }; // Red
            default:
                return { background: 'rgb(158, 158, 158)', color: '#fff' }; // Grey
        }
    };

    const getIcon = (type: string) => {
        switch (type) {
            case 'submit':
                return <ClipboardPen className="w-6 h-6" />;
            case 'signing':
                return <Handshake className="w-6 h-6" />;
            case 'payment':
                return <DollarSign className="w-6 h-6" />;
            case 'inspection':
                return <ClipboardCheck className="w-6 h-6" />;
            case 'addendum':
                return <FileText className="w-6 h-6" />;
            default:
                return <Calendar className="w-6 h-6" />;
        }
    };

    const formatStatus = (status?: string): string => {
        if (!status) return '';
        return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
    };

    const getStatusClass = (status?: string) => {
        switch (status) {
            case 'completed':
                return 'bg-success-subtle text-success';
            case 'scheduled':
                return 'bg-info-subtle text-info';
            case 'active':
                return 'bg-warning-subtle text-warning';
            case 'pending':
                return 'bg-secondary-subtle text-secondary';
            default:
                return 'bg-secondary-subtle text-secondary';
        }
    };

    const getActionButtonClass = (type: string) => {
        const baseClass = "btn btn-sm";
        switch (type) {
            case 'primary':
                return `${baseClass} btn-soft-primary`;
            case 'success':
                return `${baseClass} btn-soft-success`;
            case 'danger':
                return `${baseClass} btn-soft-danger`;
            case 'warning':
                return `${baseClass} btn-soft-warning`;
            case 'info':
                return `${baseClass} btn-soft-info`;
            default:
                return `${baseClass} btn-soft-dark`;
        }
    };

    const formatDate = (dateString: string): string => {
        // Return immediately if the string doesn't look like a date
        if (!dateString.match(/^\d{4}-\d{2}-\d{2}|^\d{4}\/\d{2}\/\d{2}/)) {
            return dateString;
        }

        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return dateString;
        }

        return date.toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const handleActionClick = (e: React.MouseEvent, onClick?: () => void) => {
        e.preventDefault();
        e.stopPropagation();
        if (onClick) {
            onClick();
        }
    };

    return (
        <div className="vertical-timeline-wrapper">
            <VerticalTimeline animate={true} className="custom-line">
                {milestones.map((milestone) => (
                    <VerticalTimelineElement
                        key={milestone.id}
                        className={`vertical-timeline-element--${milestone.type}`}
                        date={milestone.date ? formatDate(milestone.date) :
                            milestone.status === 'pending' ? 'Pending' :
                                milestone.status === 'active' ? 'In Progress' :
                                    'Not Started'
                        }
                        iconStyle={getIconStyle(milestone.type)}
                        icon={getIcon(milestone.type)}
                        position={homeownerOnRight ?
                            (milestone.assigneeType === 'homeowner' ? 'right' : 'left') :
                            (milestone.assigneeId === currentUserId ? 'right' : 'left')}
                    >
                        <div className="timeline-content">
                            <h3 className="vertical-timeline-element-title">{milestone.title}</h3>
                            {milestone.description && renderDescription(milestone.description, milestone.id)}
                            {milestone.type === 'payment' && milestone.amount && milestone.amount > 0 && (
                                <p className="timeline-amount">
                                    Amount: ${Number(milestone.amount).toFixed(2)}
                                </p>
                            )}
                            <div className="flex items-center justify-between mt-3">
                                <span className={`timeline-badge ${getStatusClass(milestone.status)}`}>
                                    {formatStatus(milestone.status)}
                                </span>
                                {milestone.action && milestone.action.show !== false && (
                                    <button
                                        type="button"
                                        onClick={(e) => handleActionClick(e, milestone.action?.onClick)}
                                        disabled={milestone.action.disabled}
                                        className={`${getActionButtonClass(milestone.action.type)} ${milestone.action.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        {milestone.action.label}
                                    </button>
                                )}
                            </div>
                            <p className="timeline-assignee">
                                Assigned to: {milestone.assigneeType === 'contractor' ? 'Contractor' : 'Homeowner'}
                            </p>
                        </div>
                    </VerticalTimelineElement>
                ))}
            </VerticalTimeline>
        </div>
    );
};

export default Timeline;